import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSheet, useSheetSection } from './Sheet';

/**
 * Files a report shows — photos, scope captures, point files — are named by
 * the file ids its context carries and turned into links by the host page,
 * which alone knows how (for a run report, presigned links scoped to the run).
 * Where no host resolves files (the studio preview, a test) a figure renders
 * as a labelled placeholder, never as a broken image.
 */
export type ReportFileResolver = (fileIds: string[]) => Promise<Record<string, string>>;

interface AssetsState {
  resolve: (fileIds: string[]) => Promise<Record<string, string>>;
}

const ReportAssetsContext = createContext<AssetsState | null>(null);

/** Resolve each file id once for the life of the page, however many figures ask. */
export function ReportAssetsProvider({ resolve, children }: { resolve: ReportFileResolver; children: ReactNode }) {
  const cache = useRef(new Map<string, Promise<string | null>>());
  const value = useMemo<AssetsState>(() => ({
    resolve: async (fileIds) => {
      const missing = fileIds.filter((id) => !cache.current.has(id));
      if (missing.length) {
        const batch = resolve(missing).catch(() => ({} as Record<string, string>));
        for (const id of missing) cache.current.set(id, batch.then((urls) => urls[id] ?? null));
      }
      const entries = await Promise.all(fileIds.map(async (id) => [id, await cache.current.get(id)] as const));
      return Object.fromEntries(entries.filter((e): e is readonly [string, string] => typeof e[1] === 'string'));
    },
  }), [resolve]);
  return <ReportAssetsContext.Provider value={value}>{children}</ReportAssetsContext.Provider>;
}

/** Links for these files; `available` is false where the host resolves no files at all. */
export function useReportFileUrls(fileIds: string[]): { urls: Record<string, string>; loading: boolean; available: boolean } {
  const assets = useContext(ReportAssetsContext);
  const key = fileIds.join(',');
  const [state, setState] = useState<{ key: string; urls: Record<string, string> } | null>(null);
  useEffect(() => {
    if (!assets || fileIds.length === 0) return;
    let cancelled = false;
    void assets.resolve(fileIds).then((urls) => { if (!cancelled) setState({ key, urls }); });
    return () => { cancelled = true; };
  }, [assets, key]);
  if (!assets) return { urls: {}, loading: false, available: false };
  const current = state?.key === key ? state.urls : null;
  return { urls: current ?? {}, loading: current === null && fileIds.length > 0, available: true };
}

export interface GalleryFigure {
  fileId: string;
  fileName: string;
  caption: ReactNode;
  /** printed as "Figure N."; leave it out and the sheet numbers the figure in print order */
  number?: number;
  /** the figure's element id, for a figure index to link to */
  anchor?: string;
  /** stable across renders, for the sheet's numbering; the file id when absent */
  key?: string;
}

/** Figures in a two-up grid, each captioned; the gallery block. Figures without a number are numbered by the sheet as they print. */
export function ReportGallery({ figures: given, title }: { figures: GalleryFigure[]; title?: ReactNode }) {
  const sheet = useSheet();
  const section = useSheetSection();
  const figures = given.map((f) => {
    if (f.number !== undefined || !sheet) return f;
    const entry = sheet.figure(f.key ?? f.fileId, { caption: f.caption, fileId: f.fileId, fileName: f.fileName, section: section?.title ?? null });
    return { ...f, number: entry.number, anchor: entry.anchor };
  });
  const { urls, loading, available } = useReportFileUrls(figures.map((f) => f.fileId));
  if (figures.length === 0) return null;
  return (
    <>
      {title && <p className="block-title">{title}</p>}
      <div className="gallery">
        {figures.map((f) => (
          <figure key={f.key ?? f.fileId} id={f.anchor} className="gallery-figure">
            {urls[f.fileId] ? (
              <a href={urls[f.fileId]} target="_blank" rel="noreferrer"><img src={urls[f.fileId]} alt={typeof f.caption === 'string' ? f.caption : f.fileName} /></a>
            ) : (
              <div className="figure-placeholder" role="img" aria-label={f.fileName}>
                <span>{f.fileName}</span>
                <span className="figure-placeholder-note">{loading ? 'Loading image…' : available ? 'Image not available' : 'Image not available in this view'}</span>
              </div>
            )}
            <figcaption>{f.number !== undefined && <b>Figure {f.number}.</b>} {f.caption}</figcaption>
          </figure>
        ))}
      </div>
    </>
  );
}

/** A file's name, linked to the file where the host can resolve it. */
export function ReportFileLink({ fileId, children }: { fileId: string; children: ReactNode }) {
  const { urls } = useReportFileUrls([fileId]);
  return urls[fileId] ? <a href={urls[fileId]} target="_blank" rel="noreferrer">{children}</a> : <>{children}</>;
}
